import { AssetTypeSummary } from '@archimed/models';
import { AssetType } from '../models/asset-type.enum';
import { TypeNode } from '../models/type-node.model';

/**
 * Relationship Tree Builder Service
 *
 * Builds a deep, expandable tree from entity types and their relationships.
 *
 * Rules:
 *  1. If entity A has a relationship to entity B, then B appears as a child of A.
 *  2. That child B is itself expandable — it shows B's own relationships as children.
 *  3. An entity that is referenced as a target by ANY relationship is never a root.
 *  4. Cycles are cut: a node never appears twice on the same branch (ancestor path).
 */
export class RelationshipTreeBuilderService {
  /**
   * Index: entityType name → its summary (for quick lookup)
   */
  private entityIndex = new Map<string, AssetTypeSummary>();

  buildTree(entityTypes: AssetTypeSummary[]): TypeNode[] {
    if (entityTypes.length === 0) {
      return [];
    }

    // Build lookup index
    this.entityIndex = new Map(entityTypes.map((et) => [et.assetType, et]));

    // Use explicitly marked roots if available
    const explicitRoots = entityTypes
      .filter((et) => et.isRoot)
      .map((et) => et.assetType);

    let rootNames: string[];

    if (explicitRoots.length > 0) {
      rootNames = explicitRoots;
    } else {
      // Collect every entity that is a target of any relationship
      const referencedTargets = this.collectReferencedTargets(entityTypes);

      // Roots = entities NOT referenced as a target anywhere
      rootNames = entityTypes
        .map((et) => et.assetType)
        .filter((name) => !referencedTargets.has(name));

      // Fallback: if everything is referenced (full cycle), pick entity
      // with the most outgoing relationships
      if (rootNames.length === 0) {
        const sorted = [...entityTypes].sort(
          (a, b) => b.relationships.length - a.relationships.length,
        );
        rootNames.push(sorted[0].assetType);
      }
    }

    // Build each root recursively
    return rootNames.map((name) => this.buildNode(name, new Set<string>()));
  }

  private buildNode(
    entityName: string,
    ancestors: Set<string>,
    relationshipType?: string,
  ): TypeNode {
    const summary = this.entityIndex.get(entityName);
    const assetType = this.mapToAssetType(entityName);
    const count = summary?.assetCount ?? 0;
    const properties = summary?.properties ?? [];

    const node = new TypeNode(
      assetType,
      entityName,
      [],
      count,
      relationshipType,
      properties.map((p) => p.name),
    );

    if (!summary || summary.relationships.length === 0) {
      return node;
    }

    const newAncestors = new Set(ancestors);
    newAncestors.add(entityName);

    for (const rel of summary.relationships) {
      if (rel.targetAssetType === entityName) {
        continue;
      }
      if (newAncestors.has(rel.targetAssetType)) {
        this.addCycleLeaf(node, rel.targetAssetType, rel.relationshipType);
      } else {
        const child = this.buildNode(
          rel.targetAssetType,
          newAncestors,
          rel.relationshipType,
        );
        node.addChild(child);
      }
    }

    return node;
  }

  private addCycleLeaf(
    parentNode: TypeNode,
    targetName: string,
    relationshipType: string,
  ): void {
    const summary = this.entityIndex.get(targetName);
    const assetType = this.mapToAssetType(targetName);
    const leaf = new TypeNode(
      assetType,
      targetName,
      [],
      summary?.assetCount ?? 0,
      relationshipType,
      summary?.properties?.map((p) => p.name) ?? [],
    );
    parentNode.addChild(leaf);
  }

  /**
   * Collect all entity types that appear as a target of any relationship
   */
  private collectReferencedTargets(
    entityTypes: AssetTypeSummary[],
  ): Set<string> {
    const targets = new Set<string>();

    for (const entityType of entityTypes) {
      for (const rel of entityType.relationships) {
        if (rel.targetAssetType !== entityType.assetType) {
          targets.add(rel.targetAssetType);
        }
      }
    }

    return targets;
  }

  private mapToAssetType(entityType: string): AssetType {
    const upperType = entityType.toUpperCase();
    if ((Object.values(AssetType) as string[]).includes(upperType)) {
      return upperType as AssetType;
    }
    return AssetType.UNKNOWN;
  }
}
